/**
 * @fileoverview Plugin for handling HTML paste operations in the Aha! Editor.
 * Converts HTML content to editor-compatible format with style cleanup and Sentry logging.
 */

import { type EditorPlugin } from '../../types/aha-editor';
import { type Descendant, type Editor, Element, Text, Transforms } from 'slate';
import produce from 'immer';
import { HtmlSerializer } from '../../serialization/HtmlSerializer';
import { getParentList, spliceFragmentIntoList } from '../ListPlugin/utils';
import AhaEditor from '../../AhaEditor';
import {
  isCommonBlockElement,
  isTableElement,
  isTableRowElement,
} from '../../types/guards';
import TableUtils from '../TablePlugin/TableUtils';
import { spliceFragmentIntoTable } from '../TablePlugin/util/spliceFragmentIntoTable';
import { AddScrubbedSentryBreadcrumb } from '../../SentryLogger';

/**
 * Removes unwanted styling from pasted HTML fragments to prevent confusion.
 * For example, we don't want to keep the blue color from links so that they
 * don't stay blue if unlinked. We also don't want background colors most of the time.
 * @param fragment - Array of Slate descendants to clean
 */
function removeUnwantedTextStyles(fragment: Descendant[]) {
  for (const element of fragment) {
    if (Element.isElement(element)) {
      if (isCommonBlockElement(element)) {
        // Remove paragraph styling.
        delete element.lineHeight;
      }

      removeUnwantedTextStyles(element.children);
    } else if (Text.isText(element)) {
      // Remove the text colors and font.
      delete element.textHighlight;
      delete element.textColor;
      delete element.fontFamily;
      delete element.fontSize;
    }
  }
}

function extractBlocksFromSingleCell(fragment: Descendant[]) {
  if (
    fragment.length === 1 &&
    isTableElement(fragment[0]) &&
    fragment[0].children.length === 1 &&
    isTableRowElement(fragment[0].children[0]) &&
    fragment[0].children[0].children.length === 1
  ) {
    if (Element.isElement(fragment[0].children[0].children[0])) {
      return fragment[0].children[0].children[0].children;
    }
    return fragment[0].children[0].children;
  }
  return fragment;
}

/**
 * Editor plugin for pasting or dropping HTML content.
 * Converts HTML to editor format, handles tables and lists, and logs operations.
 */
const PasteHtmlPlugin: EditorPlugin = {
  name: 'Paste html plugin',
  /**
   * Handles insertion of HTML data from clipboard or drag-and-drop.
   * Deserializes HTML, cleans unwanted styles, and inserts into editor.
   * @param editor - The Slate editor instance
   * @param data - DataTransfer object containing HTML data
   * @param controller - Optional editor controller for additional context
   * @returns true if handled, false if not applicable
   */
  insertData: (editor: Editor, data: DataTransfer, controller) => {
    if (data.types.includes(`application/${AhaEditor.clipboardFormatKey}`)) {
      return false; // Use the built-in fragment support, not HTML.
    }

    for (const item of data.items) {
      if (item.type === 'text/html' && item.kind === 'string') {
        const rawHtml = data.getData('text/html');
        const serializer = new HtmlSerializer(
          editor,
          controller?.serializerOptions ?? {}
        );

        let fragment = serializer.deserialize(rawHtml).children;
        // Add the serialized fragment to the breadcrumb because it's simpler
        // than scrubbing the HTML. We can consider doing that if this isn't
        // as useful for debugging pasting issues.
        AddScrubbedSentryBreadcrumb({
          message: 'Paste fragment from HTML',
          data: fragment,
        });

        fragment = extractBlocksFromSingleCell(fragment);

        fragment = produce(fragment, (draft) => {
          removeUnwantedTextStyles(draft);
        });

        // If we're in a list, use spliceFragmentIntoList to handle the paste
        if (getParentList(editor, editor.selection?.anchor)) {
          return spliceFragmentIntoList(editor, fragment);
        }

        const table = TableUtils.currentTable(editor);
        const tableSelection = controller?.state.pluginState.tableSelection;
        const fragmentIsTable =
          fragment.length === 1 && isTableElement(fragment[0]);
        if (
          table &&
          tableSelection &&
          fragmentIsTable &&
          spliceFragmentIntoTable(editor, tableSelection, fragment)
        ) {
          // spliceFragmentIntoTable handled the paste, so no need to
          // process further
          return true;
        }

        Transforms.insertFragment(editor, fragment);
        return true;
      }
    }

    return false; // This plugin didn't handle the type of data.
  },
};

export default PasteHtmlPlugin;
